import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AvgResultFarmType } from '~/api/services/dashboard'
import { Nullable } from '~/types'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatNumber } from '~/utils/helpers'
import { TooltipWrapper } from './style'
import { Divider } from 'semantic-ui-react'
import i18n from '~/components/i18n'

interface Props {
  avgResultsFarmer: Nullable<AvgResultFarmType>
}

const CustomTooltip = ({ active, payload, emissionLabels }: any) => {
  if (active && payload && payload.length) {
    return (
      <TooltipWrapper>
        {payload.map(chartValue => {
          const emission = emissionLabels[chartValue.dataKey]
          const percent = formatNumber(parseInt(chartValue.value) / parseInt(chartValue.payload.total) * 100, 2)
          return <div
            key={emission?.name}
            style={{ color: chartValue.fill || chartValue.stroke, display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <div style={{ width: 180 }}>{emission?.name}:</div>
            <div style={{ fontWeight: 'bold' }} dangerouslySetInnerHTML={{ __html: `${formatNumber(chartValue.value, 0)} (${percent}%)` }} />
          </div>
        })}
        <Divider/>
        <div
          style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}
        >
          <div style={{ width: 180 }}>{emissionLabels.total.name}</div>
          <div style={{ fontWeight: 'bold' }} dangerouslySetInnerHTML={{ __html: `${formatNumber(payload[0].payload.total, 0)} (100%)` }} />
        </div>
      </TooltipWrapper>
    );
  }
  return null;
};

export function getEmissionLabels() {
  return {
    inventoryAddition: {
      name: i18n.t('Inventory addition'),
      unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
    },
    calfRearing: {
      name: i18n.t('Calf rearing'),
      unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
    },
    feeding: {
      name: i18n.t('Feeding'),
      unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
    },
    manureAndBedding: {
      name: i18n.t('Manure and Bedding'),
      unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
    },
    waterAndEnergy: {
      name: i18n.t('Water and Energy'),
      unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
    },
    dieselProductionAndCombustion: {
      name: i18n.t('Diesel Production'),
      unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
    },
    total: {
      name: i18n.t('Total: '),
      unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]',
      info: 'Dieser Wert entspricht der Summe aller berücksichtigten Treibhausgasemissionen.'
    }
  }
}

export const emissionColors: Record<string, string> = {
  inventoryAddition: '#6f5ebc',
  calfRearing: '#5ca87d',
  feeding: '#6eb1d1',
  manureAndBedding: '#d6a943',
  waterAndEnergy: '#e19194',
  dieselProductionAndCombustion: '#a493ad'
}

const EmissionChart: React.FC<Props> = ({ avgResultsFarmer }) => {
  const { t } = useTranslation()
  
  const [emissionStackData, setEmissionStackData] = useState<any[]>([])

  const emissionLabels = getEmissionLabels()

  useEffect(() => {
    if (avgResultsFarmer) {
      const chartData = {
        name: 'THG-Emissionen (kg CO2-Äq./Kuh u. Jahr)',
        inventoryAddition: avgResultsFarmer.inventoryAddition ?? 0,
        calfRearing: avgResultsFarmer.calfRearing ?? 0,
        feeding: avgResultsFarmer.feeding ?? 0,
        manureAndBedding: avgResultsFarmer.manureAndBedding ?? 0,
        waterAndEnergy: avgResultsFarmer.waterAndEnergy ?? 0,
        dieselProductionAndCombustion: avgResultsFarmer.dieselProductionAndCombustion ?? 0,
        total: avgResultsFarmer.total ?? 0
      }
      setEmissionStackData([chartData])
    }
  }, [avgResultsFarmer, t])

  const sortedKeys = Object.keys(emissionLabels)
    .filter(key => !(key === 'total' || key === 'name'))
    .sort((a, b) => (emissionStackData[0]?.[b] ?? 0) - (emissionStackData[0]?.[a] ?? 0))

  return (
    <ResponsiveContainer width='70%' height={400}>
      <BarChart
        maxBarSize={50}
        data={emissionStackData}
        margin={{ top: 20, right: 30, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis dataKey='name' />
        <YAxis tickFormatter={(value) => formatNumber(value, 0)}/>
        <Tooltip content={<CustomTooltip emissionLabels={emissionLabels}/>}/>
        {sortedKeys.map((key, index) => {
          if(index === sortedKeys.length - 1) {
          /*   return <Bar
              key={key}
              dataKey={key}
              stackId='a'
              fill={emissionColors[key]}
              label={{ position: 'top', fontSize: 15, fill: '#000', formatter: () => formatNumber(avgResultsFarmer?.total, 0) }}
            /> */
          }
          return <Bar
            key={key}
            dataKey={key}
            stackId='a'
            fill={emissionColors[key]}
          />
        })}
      </BarChart>
    </ResponsiveContainer>
  )
}

export default EmissionChart
